/**
 * React wrapper for Bryntum TimePicker
 */
import React, { RefObject } from 'react';
import { AlignSpec, Base, Container, ContainerItemConfig, ContainerLayoutConfig, Layout, MaskConfig, MenuItemConfig, Model, Panel, PanelCollapserConfig, PanelCollapserOverlayConfig, PanelHeader, Rectangle, Scroller, ScrollerConfig, StateProvider, TabConfig, TimePicker, Tool, ToolConfig, ToolbarConfig, TooltipConfig, Widget } from '@bryntum/schedulerpro';

import { createWidget, shouldComponentUpdate, processWidgetContent } from './WrapperHelper.js';

export type BryntumTimePickerProps =  {
     // Configs only
    adopt?                   : HTMLElement|string
    align?                   : AlignSpec|string
    anchor?                  : boolean
    ariaDescription?         : string
    ariaLabel?               : string
    autoUpdateRecord?        : boolean
    bbar?                    : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    bodyCls?                 : string|object
    bubbleEvents?            : object
    callOnFunctions?         : boolean
    centered?                : boolean
    collapsed?               : boolean
    collapsible?             : boolean|Partial<PanelCollapserConfig>|Partial<PanelCollapserOverlayConfig>
    config?                  : object
    constrainTo?             : HTMLElement|Widget|Rectangle
    contentElementCls?       : string|object
    defaultBindProperty?     : string
    defaultFocus?            : (widget: Widget) => boolean
    defaults?                : object
    dock?                    : 'top'|'bottom'|'left'|'right'|'start'|'end'|'header'|'pre-header'|object
    draggable?               : boolean|{
        handleSelector?: string
    }
    floating?                : boolean
    footer?                  : {
        dock?: 'top'|'right'|'bottom'|'left'|'start'|'end'
        html?: string
        cls?: string
    }|string
    header?                  : string|boolean|PanelHeader
    hideAnimation?           : boolean|object
    hideWhenEmpty?           : boolean
    htmlCls?                 : string|object
    ignoreParentReadOnly?    : boolean
    itemCls?                 : string
    lazyItems?               : Record<string, Partial<ContainerItemConfig>>|Partial<ContainerItemConfig>[]|Widget[]
    listeners?               : object
    localeClass?             : typeof Base
    localizable?             : boolean
    localizableProperties?   : string[]
    maskDefaults?            : Partial<MaskConfig>
    masked?                  : boolean|string|Partial<MaskConfig>
    monitorResize?           : boolean
    namedItems?              : Record<string, Partial<ContainerItemConfig>>
    owner?                   : Widget
    positioned?              : boolean
    preventTooltipOnTouch?   : boolean
    relayStoreEvents?        : boolean
    ripple?                  : boolean|{
        delegate?: string
        color?: string
        radius?: number
        clip?: string
    }
    rootElement?             : ShadowRoot
    scrollAction?            : 'hide'|'realign'|null
    seconds?                 : boolean
    showAnimation?           : boolean|object
    showTooltipWhenDisabled? : boolean
    stateful?                : boolean|object|string[]
    statefulEvents?          : object|string[]
    stateId?                 : string
    stateProvider?           : StateProvider
    strips?                  : Record<string, Partial<ContainerItemConfig>>
    tab?                     : boolean|Partial<TabConfig>
    tag?                     : string
    tbar?                    : (Partial<ContainerItemConfig>|string)[]|Partial<ToolbarConfig>
    textAlign?               : 'left'|'center'|'right'|'start'|'end'
    textContent?             : boolean
    trapFocus?               : boolean
    ui?                      : string|object
    weight?                  : number

     // Configs and properties
    alignSelf?           : string
    appendTo?            : HTMLElement|string
    cls?                 : string|object
    content?             : string
    dataset?             : object
    disabled?            : boolean
    extraData?           : string|number|boolean|object
    flex?                : number|string
    format?              : string
    height?              : number|string
    hidden?              : boolean
    html?                : string|((widget: Widget) => string)
    id?                  : string
    insertBefore?        : HTMLElement|string
    insertFirst?         : HTMLElement|string
    items?               : (Partial<ContainerItemConfig>|Partial<MenuItemConfig>|Widget)[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>>|Widget[]|Record<string, Partial<ContainerItemConfig>|Partial<MenuItemConfig>|boolean|null>
    layout?              : Layout|string|Partial<ContainerLayoutConfig>
    layoutStyle?         : object
    margin?              : number|string
    max?                 : Date|string
    maxHeight?           : string|number
    maximizeOnMobile?    : number|string
    maxWidth?            : string|number
    min?                 : Date|string
    minHeight?           : string|number
    minWidth?            : string|number
    readOnly?            : boolean
    record?              : Model
    scrollable?          : Scroller|boolean|Partial<ScrollerConfig>
    strictRecordMapping? : boolean
    title?               : string
    tools?               : Record<string, Tool|Partial<ToolConfig>>|Record<string, Tool>|Record<string, Partial<ToolConfig>>
    tooltip?             : string|Partial<TooltipConfig>
    value?               : Date|string
    width?               : number|string
    x?                   : number
    y?                   : number

     // Properties only
    anchorSize?      : number[]
    initialValue?    : Date|string
    isSettingValues? : boolean
    isValid?         : boolean
    state?           : object
    type?            : string
    values?          : Record<string, object>

     // Events
    onBeforeDestroy?   : ((event: { source: Base }) => void)|string
    onBeforeHide?      : ((event: { source: Widget }) => boolean|void)|string
    onBeforeSetRecord? : ((event: { source: Container, record: Model }) => void)|string
    onBeforeShow?      : ((event: { source: Widget|any }) => Promise<boolean|void>)|string
    onCatchAll?        : ((event: {[key: string]: any, type: string}) => void)|string
    onCollapse?        : ((event: { source: Panel }) => void)|string
    onDestroy?         : ((event: { source: Base }) => void)|string
    onExpand?          : ((event: { source: Panel }) => void)|string
    onFocusIn?         : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onFocusOut?        : ((event: { source: Widget, fromElement: HTMLElement, toElement: HTMLElement, fromWidget: Widget, toWidget: Widget, backwards: boolean }) => void)|string
    onHide?            : ((event: { source: Widget }) => void)|string
    onPaint?           : ((event: { source: Widget, firstPaint: boolean }) => void)|string
    onReadOnly?        : ((event: { readOnly: boolean }) => void)|string
    onRecompose?       : (() => void)|string
    onResize?          : ((event: { source: Widget, width: number, height: number, oldWidth: number, oldHeight: number }) => void)|string
    onShow?            : ((event: { source: Widget }) => void)|string
    onTimeChange?      : ((event: { time: Date }) => void)|string
    onToolClick?       : ((event: { source: Tool, tool: Tool }) => void)|string

}

export class BryntumTimePicker extends React.Component<BryntumTimePickerProps> {

    static instanceClass = TimePicker;

    static instanceName = 'TimePicker';

    processWidgetContent = processWidgetContent;

    static configNames = [
        'adopt',
        'align',
        'anchor',
        'ariaDescription',
        'ariaLabel',
        'autoUpdateRecord',
        'bbar',
        'bodyCls',
        'bubbleEvents',
        'callOnFunctions',
        'centered',
        'collapsed',
        'collapsible',
        'config',
        'constrainTo',
        'contentElementCls',
        'defaultBindProperty',
        'defaultFocus',
        'defaults',
        'dock',
        'draggable',
        'floating',
        'footer',
        'header',
        'hideAnimation',
        'hideWhenEmpty',
        'htmlCls',
        'ignoreParentReadOnly',
        'itemCls',
        'lazyItems',
        'listeners',
        'localeClass',
        'localizable',
        'localizableProperties',
        'maskDefaults',
        'masked',
        'monitorResize',
        'namedItems',
        'owner',
        'positioned',
        'preventTooltipOnTouch',
        'relayStoreEvents',
        'ripple',
        'rootElement',
        'scrollAction',
        'seconds',
        'showAnimation',
        'showTooltipWhenDisabled',
        'stateful',
        'statefulEvents',
        'stateId',
        'stateProvider',
        'strips',
        'tab',
        'tag',
        'tbar',
        'textAlign',
        'textContent',
        'trapFocus',
        'ui',
        'weight'
    ];

    static propertyConfigNames = [
        'alignSelf',
        'appendTo',
        'cls',
        'content',
        'dataset',
        'disabled',
        'extraData',
        'flex',
        'format',
        'height',
        'hidden',
        'html',
        'id',
        'insertBefore',
        'insertFirst',
        'items',
        'layout',
        'layoutStyle',
        'margin',
        'max',
        'maxHeight',
        'maximizeOnMobile',
        'maxWidth',
        'min',
        'minHeight',
        'minWidth',
        'onBeforeDestroy',
        'onBeforeHide',
        'onBeforeSetRecord',
        'onBeforeShow',
        'onCatchAll',
        'onCollapse',
        'onDestroy',
        'onExpand',
        'onFocusIn',
        'onFocusOut',
        'onHide',
        'onPaint',
        'onReadOnly',
        'onRecompose',
        'onResize',
        'onShow',
        'onTimeChange',
        'onToolClick',
        'readOnly',
        'record',
        'scrollable',
        'strictRecordMapping',
        'title',
        'tools',
        'tooltip',
        'value',
        'width',
        'x',
        'y'
    ];

    static propertyNames = [
        'anchorSize',
        'initialValue',
        'isSettingValues',
        'isValid',
        'state',
        'type',
        'values'
    ];

    // Component instance
    instance!: TimePicker;

    // Component element
    element! : HTMLElement;

    componentDidMount(): void {
        this.instance = createWidget(this);
    }

    componentWillUnmount(): void {
        // @ts-ignore
        this.instance?.destroy?.();
    }

    /**
     * Component about to be updated, from changing a prop using state.
     * React to it depending on what changed and prevent react from re-rendering our component.
     * @param nextProps
     * @param nextState
     * @returns {boolean}
     */
    shouldComponentUpdate(nextProps: Readonly<BryntumTimePickerProps>, nextState: Readonly<{}>): boolean {
        return shouldComponentUpdate(this, nextProps, nextState);
    }

    render(): React.ReactNode {

        const className = `b-react-time-picker-container`;
        return (
            <div className={className} ref={(element) => (this.element = element!)}></div>
        );

    }
}
